import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import FullpageWrapper from '@/components/FullpageWrapper';
import IframeContainer from '@/components/IframeContainer';
import { loadLocation, setIsNewsTickerVisible } from '@/state/features/app';
import { useAppDispatch, useAppSelector } from '@/state/store';
import AchievementFeed from '@/components/AchievementFeed';
import FloatingHeaderBar from '@/components/FloatingHeaderBar';
import ProfileDrawerV2 from '@/components/ProfileDrawerV2';
import MediaViewer from '@/components/MediaViewer';
import NewsTicker from '@/components/NewsTicker';
import InformationDrawer from '@/components/InformationDrawer';
import MyRewardsDrawer from '@/components/MyRewardsDrawer';
import ExitModal from '@/components/ExitModal';
import ShareModal from '@/components/ShareModal';
import GenericLeaderboard from '@/components/GenericLeaderboard';
import { sendGraphicsCapabilityEvent, sendPageViewEvent } from '@/utils/analytics/events';
import { homePath } from '@/theme/vars';
import { useTheme } from '@/theme';
import { heartbeat } from '@/arise/api';
import { pathToURL } from '@/utils/urls';
import { useUITranslatedText } from '@/components/TranslatedText';

import * as styles from './styles.module.scss';
import { IS_DISCORD_ACTIVITY } from '@/globals';
import DiscordAuthFlow, { DiscordAuthFlowMethods } from '@/components/DiscordAuthFlow';

export default function LocationPageBasic() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const discordRef = useRef<DiscordAuthFlowMethods>();

    const theme = useTheme();
    const {
        location,
        isLocationLoading,
        isProfileOpen,
        isInformationOpen,
        isMyRewardsOpen,
        locationLoadErrorId,
        shouldHideUi,
    } = useAppSelector((state) => state.app);
    const loggedIn = useAppSelector((state) => state.auth.loggedIn);
    const { locationId: id } = useParams();

    useEffect(() => {
        // Redirect if not found
        if (locationLoadErrorId === id) {
            navigate(homePath);
        }
    }, [locationLoadErrorId]);

    useEffect(() => {
        if ((!location || location.id !== id) && !isLocationLoading) dispatch(loadLocation(id));
        sendPageViewEvent();
        sendGraphicsCapabilityEvent();

        // Reset UI on load
        dispatch(setIsNewsTickerVisible(!!theme.metadata?.customJSON?.newsTicker?.showImmediately));
    }, [id]);

    useEffect(() => {
        let interval;
        let timeout;

        if (id && loggedIn) {
            // Begin heartbeat after 2 seconds
            timeout = setTimeout(() => {
                heartbeat(id);
                interval = setInterval(() => {
                    heartbeat(id);
                }, 30 * 1000); // 30s
            }, 2000);
        }

        return () => {
            if (timeout) clearTimeout(timeout);
            if (interval) clearInterval(interval);
        };
    }, [id, loggedIn]);

    // Loading screen text translations
    const loadingScreenData = theme?.metadata?.customJSON?.loading;
    const translation_loading1Title = useUITranslatedText('loading_message1Title', loadingScreenData?.message1?.title);
    const translation_loading1Subtitle = useUITranslatedText(
        'loading_message1Subtitle',
        loadingScreenData?.message1?.subtitle,
    );
    const translation_loading2Title = useUITranslatedText('loading_message2Title', loadingScreenData?.message2?.title);
    const translation_loading2Subtitle = useUITranslatedText(
        'loading_message2Subtitle',
        loadingScreenData?.message2?.subtitle,
    );
    const translation_loading3Title = useUITranslatedText('loading_message3Title', loadingScreenData?.message3?.title);
    const translation_loading3Subtitle = useUITranslatedText(
        'loading_message3Subtitle',
        loadingScreenData?.message3?.subtitle,
    );

    if (!location || location.id !== id) return <></>;

    const sidebarIsOpen = (isProfileOpen || isInformationOpen || isMyRewardsOpen) && !shouldHideUi;
    const hasProfileDrawer = theme.metadata?.customJSON?.profileDrawerV2?.enabled;
    const hasInformationDrawer = theme.metadata?.customJSON?.informationDrawer?.enabled;
    const hasNewsTicker = theme.metadata?.customJSON?.newsTicker?.enabled;
    const hasMenu = theme.metadata?.customJSON?.menu?.enabled;
    const hasRewardsDrawer = theme.metadata?.customJSON?.rewardsDrawer?.enabled;

    return (
        <FullpageWrapper>
            <div
                className={classNames(styles.contentWrapper, {
                    [styles['contentWrapper--sidebarOpen']]: sidebarIsOpen,
                })}
            >
                <IframeContainer
                    forceInFrontOfUi={shouldHideUi}
                    src={location.iframeURL || location.latestArisewareBuildURL}
                    arisewareProjectID={location.arisewareProjectID}
                    onReady={() => {}}
                    loadingBackgroundImageUrl={pathToURL(loadingScreenData?.backgroundImagePath)}
                    loadingLogoImageUrl={pathToURL(loadingScreenData?.logoImagePath)}
                    loadingText={[
                        { title: translation_loading1Title, subtitle: translation_loading1Subtitle },
                        { title: translation_loading2Title, subtitle: translation_loading2Subtitle },
                        { title: translation_loading3Title, subtitle: translation_loading3Subtitle },
                    ]}
                    discordRef={discordRef}
                />
                {hasNewsTicker && <NewsTicker />}
                {hasMenu && <FloatingHeaderBar />}
            </div>
            <div
                className={classNames(styles.sidebarWrapper, {
                    [styles['sidebarWrapper--sidebarOpen']]: sidebarIsOpen,
                })}
            >
                {hasProfileDrawer && <ProfileDrawerV2 />}
                {hasInformationDrawer && <InformationDrawer />}
                {hasRewardsDrawer && <MyRewardsDrawer />}
            </div>
            <GenericLeaderboard />
            <MediaViewer />
            <AchievementFeed />
            <ShareModal />
            <ExitModal />

            {IS_DISCORD_ACTIVITY && <DiscordAuthFlow ref={discordRef} />}
        </FullpageWrapper>
    );
}
